// Renders the legacy carpenter `fa fa-{name}` markup as an inline FA Pro 6
// `<svg><use>` of the static Charge CDN assets, matching what the PHP `fa()`
// helper (Libs/ViewUtils.php) emits. This lets the last runtime emitters of
// raw FA4 classes drop the icomoon webfont (#61998 / #62040).
//
// The map is the subset of iFixit\Framework\Icons\FaIconLegacyMap that
// carpenter JS still emits, plus a few names no PHP `fa()` call uses
// (bold/italic/underline/header/image/sort/circle-o/smile-o). FA4 names are
// frozen, so this doesn't drift; keep it in sync with that PHP map.

export type FaLegacyStyle = 'solid' | 'regular';

const FA4_TO_FA6: Record<string, { style: FaLegacyStyle; name: string }> = {
   'angle-left': { style: 'solid', name: 'angle-left' },
   'angle-right': { style: 'solid', name: 'angle-right' },
   'arrow-left': { style: 'solid', name: 'arrow-left' },
   'arrow-right': { style: 'solid', name: 'arrow-right' },
   bold: { style: 'solid', name: 'bold' },
   certificate: { style: 'solid', name: 'certificate' },
   check: { style: 'solid', name: 'check' },
   'check-circle': { style: 'solid', name: 'circle-check' },
   circle: { style: 'solid', name: 'circle' },
   // FA4 `-o` outline variants map to Regular.
   'circle-o': { style: 'regular', name: 'circle' },
   'exclamation-circle': { style: 'solid', name: 'circle-exclamation' },
   'file-image-o': { style: 'regular', name: 'file-image' },
   'file-pdf-o': { style: 'regular', name: 'file-pdf' },
   'file-video-o': { style: 'regular', name: 'file-video' },
   gift: { style: 'solid', name: 'gift' },
   // FA4 `header` was renamed `heading` in FA Pro 6.
   header: { style: 'solid', name: 'heading' },
   image: { style: 'solid', name: 'image' },
   italic: { style: 'solid', name: 'italic' },
   link: { style: 'solid', name: 'link' },
   'list-ol': { style: 'solid', name: 'list-ol' },
   'list-ul': { style: 'solid', name: 'list-ul' },
   mobile: { style: 'solid', name: 'mobile' },
   'pencil-square-o': { style: 'regular', name: 'pen-to-square' },
   plus: { style: 'solid', name: 'plus' },
   'question-circle': { style: 'solid', name: 'circle-question' },
   'search-minus': { style: 'solid', name: 'magnifying-glass-minus' },
   'search-plus': { style: 'solid', name: 'magnifying-glass-plus' },
   'shopping-cart': { style: 'solid', name: 'cart-shopping' },
   'smile-o': { style: 'regular', name: 'face-smile' },
   sort: { style: 'solid', name: 'sort' },
   times: { style: 'solid', name: 'xmark' },
   trash: { style: 'solid', name: 'trash' },
   'trash-o': { style: 'regular', name: 'trash-can' },
   underline: { style: 'solid', name: 'underline' },
   // FA4 `video-camera` was renamed `video` in FA Pro 6.
   'video-camera': { style: 'solid', name: 'video' },
};

// Same URL GuideURI::faIconHref() and the FaIcon React component build. The
// `?v=3` cache-buster keeps all three fetching the one cached asset.
function faIconHref(name: string, style: FaLegacyStyle): string {
   return `/_next/charge/icons/next/classic/${style}/${name}.svg?v=3#${name}`;
}

// Resolves a legacy FA4 name to the FA Pro 6 { style, name } the FaIcon React
// component takes, or null when the name isn't one carpenter JS emits.
export function resolveFaLegacyIcon(
   fa4Name: string
): { style: FaLegacyStyle; name: string } | null {
   return FA4_TO_FA6[fa4Name] ?? null;
}

// The inner `<svg><use>` markup for a legacy FA4 name, matching the SVG that
// PHP `fa()` nests inside its `<i class="fa fa-{name} fa-svg">` wrapper. Inject
// this into an existing `.fa fa-{name}` element after adding the `fa-svg` class
// (base.less keys the layout/fill and webfont-glyph suppression on `fa-svg`).
// Empty string for unmapped names.
export function faLegacyIconSvg(fa4Name: string): string {
   const resolved = FA4_TO_FA6[fa4Name];
   if (!resolved) {
      return '';
   }
   const href = faIconHref(resolved.name, resolved.style);
   return `<svg width="1em" height="1em" aria-hidden="true" focusable="false"><use href="${href}" /></svg>`;
}

// The full `<i class="fa fa-{name} fa-svg …">…</i>` markup PHP `fa()` emits, for
// string-render sites that build innerHTML (e.g. `el.set('html', …)`). Empty
// string for unmapped names.
export function faLegacyIconMarkup(fa4Name: string, extraClasses = ''): string {
   const svg = faLegacyIconSvg(fa4Name);
   if (!svg) {
      return '';
   }
   const classString = `fa fa-${fa4Name} fa-svg${extraClasses ? ` ${extraClasses}` : ''}`;
   return `<i class="${classString}">${svg}</i>`;
}
